import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, BookOpen, FileText, Cpu, Sparkles, ArrowRight } from "lucide-react";

export default function Navbar({ theme = "dark" }) {
  const isLight = theme === "light";
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(true);
  const [modalState, setModalState] = useState({ isOpen: false, title: "", description: "" });
  
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 25) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHomePage = location.pathname === "/" || location.pathname === "/portfolio" || location.pathname === "/portfolio/";

  const standardNavLinks = [
    { label: "Home", href: "/" },
    { label: "Expertise", href: "/#expertise" },
    { label: "Case Studies", href: "/#case-studies" },
    { label: "Experience", href: "/#experience" },
  ];

  const resourceSubmenu = [
    {
      label: "AI Visibility Audit",
      description: "Full 4-layer audit to get cited in ChatGPT & Claude",
      icon: Sparkles,
      badge: "₹5,000",
      isComingSoon: false,
      href: "/ai-visibility-audit/",
    },
    {
      label: "Blog",
      description: "AI Search insights & SEO thought leadership",
      icon: BookOpen,
      badge: "New",
      isComingSoon: false,
      href: "/blog/",
    },
    {
      label: "Simulator",
      description: "Interactive AI Search & SEO audit readiness tester",
      icon: Cpu,
      badge: "Interactive",
      isComingSoon: false,
      href: isHomePage ? "#simulator" : "/#simulator",
    },
  ];

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setResourcesOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setResourcesOpen(false);
    }, 150);
  };

  const handleResourceClick = (item) => {
    setResourcesOpen(false);
    if (item.isComingSoon) {
      setModalState({
        isOpen: true,
        title: item.label,
        description:
          item.label === "Blog"
            ? "Narendra's in-depth blog on B2B SaaS organic growth, AI Overviews, and AEO optimization is launching soon. Check back shortly for deep-dive frameworks and tactical breakdowns!"
            : "Exclusive downloadable B2B SEO blueprints, site migration checklists, and HEO playbooks are currently in production and will be available to download soon!",
      });
    }
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isLight
            ? (isScrolled 
                ? "py-3 bg-white/95 backdrop-blur-md border-b border-slate-200/90 shadow-md shadow-slate-900/5" 
                : "py-5 bg-white/70 backdrop-blur-sm border-b border-slate-200/50")
            : (isScrolled 
                ? "py-3 bg-[#0a0a0d]/90 backdrop-blur-md border-b border-white/10 shadow-2xl shadow-black/40" 
                : "py-5 bg-transparent")
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Brand / Logo */}
          <Link 
            to="/" 
            onClick={() => {
              if (isHomePage) window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 group focus:outline-none"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4f46e5] to-[#06b6d4] flex items-center justify-center font-heading font-black text-white text-lg tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.35)] group-hover:scale-105 group-hover:shadow-[0_0_25px_rgba(79,70,229,0.5)] transition-all duration-200">
              KN
            </div>
            <div className="flex flex-col">
              <span className={`font-heading font-bold text-sm tracking-tight transition-colors ${
                isLight ? "text-slate-900 group-hover:text-blue-600" : "text-white group-hover:text-[#38bdf8]"
              }`}>
                KANNA NARENDRA
              </span>
              <span className={`text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 mt-1 ${
                isLight ? "text-slate-500" : "text-white/50"
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                AEO/GEO & SEO Consultant
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links & CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <nav className={`flex items-center gap-1 px-3 py-1.5 rounded-full backdrop-blur-md transition-colors ${
              isLight ? "bg-slate-100/90 border border-slate-200/90 shadow-sm" : "bg-white/[0.04] border border-white/[0.08]"
            }`}>
            {standardNavLinks.map((link) => (
              isHomePage ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    if (link.label === "Home") {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={`text-xs uppercase tracking-wider font-semibold px-3.5 py-1.5 rounded-full transition-all duration-150 ${
                    isLight 
                      ? "text-slate-600 hover:text-slate-950 hover:bg-white" 
                      : "text-white/70 hover:text-white hover:bg-white/[0.08]"
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-xs uppercase tracking-wider font-semibold px-3.5 py-1.5 rounded-full transition-all duration-150 ${
                    isLight 
                      ? "text-slate-600 hover:text-slate-950 hover:bg-white" 
                      : "text-white/70 hover:text-white hover:bg-white/[0.08]"
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}

            {/* Resources with Submenu */}
            <div 
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className={`flex items-center gap-1 text-xs uppercase tracking-wider font-semibold px-3.5 py-1.5 rounded-full transition-all duration-150 focus:outline-none ${
                  isLight
                    ? (resourcesOpen ? "text-slate-950 bg-white shadow-sm" : "text-slate-600 hover:text-slate-950 hover:bg-white")
                    : (resourcesOpen ? "text-white bg-white/[0.1] shadow-[0_0_15px_rgba(6,182,212,0.25)]" : "text-white/70 hover:text-white hover:bg-white/[0.08]")
                }`}
                aria-expanded={resourcesOpen}
                aria-haspopup="true"
              >
                <span>Resources</span>
                <ChevronDown 
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    resourcesOpen 
                      ? (isLight ? "rotate-180 text-blue-600" : "rotate-180 text-cyan-400") 
                      : (isLight ? "text-slate-400" : "text-white/50")
                  }`} 
                />
              </button>

              {/* Submenu Dropdown Container */}
              {resourcesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 w-80 z-50 animate-in fade-in zoom-in-95 duration-150">
                  <div className={`backdrop-blur-2xl rounded-2xl p-2 shadow-2xl overflow-hidden ${
                    isLight 
                      ? "bg-white/98 border border-slate-200 ring-1 ring-slate-200/50 shadow-slate-900/10" 
                      : "bg-[#0b0e17]/95 border border-white/10 shadow-black/90 ring-1 ring-cyan-500/20"
                  }`}>
                    <div className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest border-b flex items-center justify-between ${
                      isLight ? "text-blue-700 border-slate-100" : "text-cyan-400/80 border-white/[0.06]"
                    }`}>
                      <span>Knowledge & Tools</span>
                      <Sparkles className={`w-3 h-3 ${isLight ? "text-blue-600" : "text-cyan-400"}`} />
                    </div>
                    
                    <div className="flex flex-col gap-1 mt-1.5">
                      {resourceSubmenu.map((item) => {
                        const Icon = item.icon;
                        const itemContent = (
                          <div className={`flex items-start gap-3 p-2.5 rounded-xl border border-transparent transition-all duration-150 group/item text-left w-full cursor-pointer ${
                            isLight ? "hover:bg-slate-50 hover:border-slate-200" : "hover:bg-white/[0.07] hover:border-white/10"
                          }`}>
                            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center group-hover/item:scale-105 transition-all shrink-0 ${
                              isLight 
                                ? "bg-blue-50 border-blue-200/60 text-blue-600 group-hover/item:text-blue-700" 
                                : "bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border-white/10 text-cyan-400 group-hover/item:text-cyan-300"
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold transition-colors ${
                                  isLight ? "text-slate-900 group-hover/item:text-blue-600" : "text-white group-hover/item:text-cyan-400"
                                }`}>
                                  {item.label}
                                </span>
                                {item.badge && (
                                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                                    item.isComingSoon 
                                      ? (isLight ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-amber-500/10 text-amber-300 border border-amber-500/20")
                                      : (isLight ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20")
                                  }`}>
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                              <span className={`text-[11px] leading-snug mt-0.5 truncate ${
                                isLight ? "text-slate-500 group-hover/item:text-slate-700" : "text-white/50 group-hover/item:text-white/70"
                              }`}>
                                {item.description}
                              </span>
                            </div>
                          </div>
                        );

                        if (item.isComingSoon) {
                          return (
                            <button
                              key={item.label}
                              type="button"
                              onClick={() => handleResourceClick(item)}
                              className="text-left w-full focus:outline-none"
                            >
                              {itemContent}
                            </button>
                          );
                        }

                        return isHomePage ? (
                          <a
                            key={item.label}
                            href={item.href}
                            onClick={() => setResourcesOpen(false)}
                            className="focus:outline-none"
                          >
                            {itemContent}
                          </a>
                        ) : (
                          <Link
                            key={item.label}
                            to={item.href}
                            onClick={() => setResourcesOpen(false)}
                            className="focus:outline-none"
                          >
                            {itemContent}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact */}
            <Link
              to="/#contact"
              onClick={() => {
                if (isHomePage) {
                  const el = document.getElementById("contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className={`text-xs uppercase tracking-wider font-semibold px-3.5 py-1.5 rounded-full transition-all duration-150 ${
                isLight 
                  ? "text-slate-600 hover:text-slate-950 hover:bg-white" 
                  : "text-white/70 hover:text-white hover:bg-white/[0.08]"
              }`}
            >
              Contact
            </Link>
            </nav>
            {/* Desktop AI Visibility Audit CTA */}
            <Link
              to="/ai-visibility-audit/"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.35)] hover:shadow-[0_0_28px_rgba(6,182,212,0.55)] transition-all duration-200 border border-cyan-400/40 group shrink-0"
            >
              <Sparkles className="w-3.5 h-3.5 text-cyan-200 group-hover:rotate-12 transition-transform duration-200" />
              <span>AI Visibility Audit</span>
              <span className="text-[10px] font-mono font-semibold px-1.5 py-0.2 rounded bg-cyan-400/20 text-cyan-200 border border-cyan-300/30">₹5,000</span>
            </Link>
          </div>

          {/* Mobile menu trigger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg border transition-colors ${
              isLight 
                ? "bg-slate-100 border-slate-200 text-slate-800 hover:bg-slate-200" 
                : "bg-white/[0.06] border border-white/[0.1] text-white hover:bg-white/[0.12]"
            }`}
            aria-label="Toggle navigation drawer menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Drawer Menu */}
        {mobileMenuOpen && (
          <div className={`lg:hidden border-b px-6 py-6 mt-3 space-y-4 backdrop-blur-2xl shadow-2xl animate-in slide-in-from-top duration-200 ${
            isLight ? "bg-white/98 border-slate-200 text-slate-900" : "bg-[#0c0c10]/98 border-white/10"
          }`}>
            <div className="flex flex-col space-y-2">
              {standardNavLinks.map((link) => (
                isHomePage ? (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (link.label === "Home") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                      isLight ? "text-slate-700 hover:text-slate-950 hover:bg-slate-100" : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                    }`}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                      isLight ? "text-slate-700 hover:text-slate-950 hover:bg-slate-100" : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {/* Mobile Resources Submenu */}
              <div className={`pt-2 pb-1 border-t ${isLight ? "border-slate-200" : "border-white/10"}`}>
                <button
                  type="button"
                  onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    isLight ? "text-slate-900 hover:bg-slate-100" : "text-white hover:bg-white/[0.06]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>Resources</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                      isLight ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                    }`}>{resourceSubmenu.length}</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileResourcesOpen ? (isLight ? "rotate-180 text-blue-600" : "rotate-180 text-cyan-400") : (isLight ? "text-slate-400" : "text-white/50")}`} />
                </button>

                {mobileResourcesOpen && (
                  <div className="mt-2 ml-2 pl-3 border-l border-white/10 space-y-1.5">
                    {resourceSubmenu.map((item) => {
                      const Icon = item.icon;
                      if (item.isComingSoon) {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              handleResourceClick(item);
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors text-left"
                          >
                            <div className="flex items-center gap-2.5">
                              <Icon className="w-4 h-4 text-cyan-400" />
                              <span>{item.label}</span>
                            </div>
                            {item.badge && (
                              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                {item.badge}
                              </span>
                            )}
                          </button>
                        );
                      }

                      return isHomePage ? (
                        <a
                          key={item.label}
                          href={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center justify-between px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className="w-4 h-4 text-cyan-400" />
                            <span>{item.label}</span>
                          </div>
                          {item.badge && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                              {item.badge}
                            </span>
                          )}
                        </a>
                      ) : (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center justify-between px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className="w-4 h-4 text-cyan-400" />
                            <span>{item.label}</span>
                          </div>
                          {item.badge && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Contact */}
              <Link
                to="/#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-white/80 hover:text-white hover:bg-white/[0.06] px-3 py-2 rounded-lg transition-colors"
              >
                Contact
              </Link>

              {/* Mobile AI Visibility Audit CTA */}
              <div className="pt-2">
                <Link
                  to="/ai-visibility-audit/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 text-white font-bold text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.4)] border border-cyan-400/40 transition-transform active:scale-95"
                >
                  <Sparkles className="w-4 h-4 text-cyan-200" />
                  <span>AI Visibility Audit · ₹5,000</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Resource Coming Soon Modal */}
      {modalState.isOpen && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setModalState({ isOpen: false, title: "", description: "" })}
        >
          <div 
            className="relative w-full max-w-md bg-[#0c101d] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-cyan-500/10 ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white tracking-tight">{modalState.title}</h3>
                  <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">Coming Soon</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalState({ isOpen: false, title: "", description: "" })}
                className="p-1 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="py-4">
              <p className="text-sm text-white/70 leading-relaxed">
                {modalState.description}
              </p>
              <div className="mt-4 p-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-xs text-white/60 flex items-start gap-2.5">
                <Cpu className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>
                  In the meantime, you can test your SaaS website right now using the <strong className="text-white">AI Search & SEO Simulator</strong>!
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setModalState({ isOpen: false, title: "", description: "" })}
                className="px-4 py-2 text-xs font-semibold text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                Dismiss
              </button>
              <a
                href={isHomePage ? "#simulator" : "/#simulator"}
                onClick={() => setModalState({ isOpen: false, title: "", description: "" })}
                className="px-4 py-2 text-xs font-semibold bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-400 hover:to-cyan-400 text-white rounded-lg shadow-lg shadow-cyan-500/20 transition-all flex items-center gap-1.5"
              >
                <span>Launch Simulator</span>
                <Cpu className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
